import React, { useState, useEffect } from "react";
import { useAuth0 } from "react-auth0-spa";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  apiRequest,
  ApiCalls,
} from "../../forms/providerAppointmentForms/AllAppointmentsApi";
import DocumentViewer from "./DocumentViewer";
import "./ClientDocuments.scss";

interface DocumentResponse {
  eventId: number;
  hasNote: boolean;
  noteStatus: string;
  noteId: number;
  startDate: string;
  appointmentTypeName: string;
  clientName: string;
  clientId: number;
}

interface Client {
  id: number;
  name: string;
}

interface ApiResponse {
  clients: Client[];
  documents: DocumentResponse[][];
}

interface Document {
  id: string;
  name: string;
  type: string;
  url?: string;
  date?: string;
  noteId?: number;
  eventId?: number;
  clientName?: string;
  clientId?: number;
}

interface ClientDocumentsProps {
  clientId?: number;
  showAll?: boolean;
  clientEmail?: string;
}

const ClientDocuments: React.FC<ClientDocumentsProps> = ({
  clientId: _clientId,
  showAll: _showAllProp = false,
  clientEmail,
}) => {
  const { getTokenSilently } = useAuth0();
  const navigate = useNavigate();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);

  useEffect(() => {
    const fetchDocuments = async () => {
      console.log("[ClientDocuments] clientEmail:", clientEmail);
      
      if (!clientEmail) {
        console.log("[ClientDocuments] No clientEmail, skipping fetch");
        setLoading(false);
        return;
      }

      try {
        const accessToken = await getTokenSilently();
        const config = {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        };

        console.log("[ClientDocuments] Making API call to:", `${apiRequest}/${ApiCalls.getAllApprovedBloomDocuments}`);
        console.log("[ClientDocuments] Payload:", { email: clientEmail });

        const response = await axios.post(
          `${apiRequest}/${ApiCalls.getAllApprovedBloomDocuments}`,
          { email: clientEmail },
          config
        );

        console.log("[ClientDocuments] API Response:", response);
        console.log("[ClientDocuments] Response data:", response.data);
        console.log("[ClientDocuments] Response data type:", typeof response.data);

        // Handle null or empty response from API
        if (!response.data) {
          console.log("[ClientDocuments] No documents found for this email");
          setDocuments([]);
          setClients([]);
          setError(false);
          setLoading(false);
          return;
        }

        // The API returns an array with a single object: [{ clients: [...], documents: [...] }]
        const rawData = Array.isArray(response.data) ? response.data[0] : response.data;
        const apiData = rawData as ApiResponse;
        console.log("[ClientDocuments] apiData (after unwrapping):", apiData);
        console.log("[ClientDocuments] apiData.clients:", apiData?.clients);
        console.log("[ClientDocuments] apiData.documents:", apiData?.documents);

        // Handle case where apiData is undefined
        if (!apiData) {
          console.log("[ClientDocuments] apiData is undefined");
          setDocuments([]);
          setClients([]);
          setError(false);
          setLoading(false);
          return;
        }

        // Store clients for navigation
        if (apiData.clients) {
          setClients(apiData.clients);
          console.log("[ClientDocuments] Clients set:", apiData.clients);
        }

        // Flatten all documents from all clients
        const allDocuments: Document[] = [];
        if (apiData.documents && Array.isArray(apiData.documents)) {
          console.log("[ClientDocuments] documents is array, length:", apiData.documents.length);
          apiData.documents.forEach((clientDocs: DocumentResponse[], index: number) => {
            console.log(`[ClientDocuments] Processing client ${index}, docs:`, clientDocs);
            if (Array.isArray(clientDocs)) {
              clientDocs.forEach((doc: DocumentResponse) => {
                console.log("[ClientDocuments] Adding doc:", doc);
                allDocuments.push({
                  id: doc.noteId.toString(),
                  name: doc.appointmentTypeName,
                  type: "PDF",
                  date: doc.startDate,
                  noteId: doc.noteId,
                  eventId: doc.eventId,
                  clientName: doc.clientName,
                  clientId: doc.clientId,
                });
              });
            }
          });
        } else {
          console.log("[ClientDocuments] documents is NOT an array or is undefined");
        }

        console.log("[ClientDocuments] Final allDocuments:", allDocuments);
        setDocuments(allDocuments);
        setError(false);
      } catch (err) {
        console.error("[ClientDocuments] Error fetching documents:", err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchDocuments();
  }, [clientEmail, getTokenSilently]);

  const handleSeeAllDocuments = () => {
    // Navigate to all documents page with data
    navigate("/documents", {
      state: {
        documents,
        clients,
        clientEmail,
      },
    });
  };

  return (
    <DocumentViewer
      documents={documents}
      clients={clients}
      loading={loading}
      error={error}
      isAllDocuments={false}
      showBreadcrumbs={false}
      maxDisplayDocuments={2}
      onSeeAllDocuments={handleSeeAllDocuments}
      clientEmail={clientEmail}
    />
  );
};

export default ClientDocuments;
